import React from "react";
import { Box, Stack } from "@mui/material";
import ActionButton, { TextButtonExtraLarge } from "../../components/commonComponents/Buttons/ActionButton";
import {
  Add,
  Input,
  Output,
  OndemandVideo,
  Router,
  Settings,
  ArrowDropDown,
  ArrowDropUp,
  CheckCircle,
  Cancel,
  Download,
} from "@mui/icons-material";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  IconText,
  IconTextDisabled,
  StyledMainBox,
} from "../../components/commonComponents/StyledComponents/styled";
import SimpleModal from "../../components/commonComponents/Modals/SimpleModal";
import AddStoreWizard from "./ModalContent/AddStoreWizard";
import { useNavigate } from "react-router-dom";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import {
  fetchCountriesList,
  fetchIpAddressForFilter,
  fetchOpeningHours,
  fetchScheduleOverviewsBulk,
  fetchStoreLocations,
  fetchStoreNames,
  manageStoresPaginationCookie,
  reset_infrastructure_config_page_data,
  sortExcelStoreLocations,
} from "../../api_calls/infrastructure";
import { useDispatch, useSelector } from "react-redux";
import { checkTodayDateWithFormat, SortIcon, WEEKDAY_LOWER_TO_INT } from "../../api_calls/utils";
import { loadRegions } from "../../api_calls/campaigns";
import { deleteItems, updateStateData } from "../../Store/actions";
import { checkUserDetailsFromCookie, checkUserPermissions } from "../../api_calls/userManagement";
import InfraFilters from "./InfraFilter";
import { fetchOwnerNames } from "../../api_calls/campaignRequests";
import * as xlsx from "xlsx";
import { useListingTableController } from "../../hooks/useListingTableController";
import ListingDataTable from "../../components/commonComponents/TabularLists/ListingDataTable";
import { ListingCellCenter, ListingCellLeft, ListingCellRight, ListingHeaderContent, ListingIconMetric, ListingMetricValue } from "../../components/commonComponents/TabularLists/styles";
import { setBatchLoadingStatus } from "../../api_calls/requestConfig";

const Locations = () => {
  const infraDetails = useSelector((state) => state.userSelections);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  let availableToUserTypes = ["cyreen", "branch"];
  const loggedUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  // Schedule Manager has infra_store_list_can_edit=true (opening hours) but
  // infra_store_monitoring_can_view=false. Creating stores is full infra access only.
  const canAddStore =
    permissionScope.infra_store_list_can_edit === true &&
    permissionScope.infra_store_monitoring_can_view === true;
  const [sortingParam, setSortingParam] = React.useState({
    cyreenId: true,
    store_name: false,
  });
  const [sortingMeasure, setSortingMeasure] = React.useState({
    ipAddress: "asc",
    storeName: "asc",
  });
  const [listLoaded, setListLoaded] = React.useState(false);

  const navigate = useNavigate();
  const dispatchAction = useDispatch();

  // store locations records to be displayed in the listing table
  let storeLocListitems =
    typeof infraDetails.infra_store_locs !== "undefined" && infraDetails.infra_store_locs.length !== 0
      ? infraDetails.infra_store_locs
      : [];

  const closeModal = () => {
    dispatchAction(deleteItems(["new_store_loc_data"]));
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };


  const handleSortingParamChange = (sortField, sortOrder) => {
    setSortingMeasure((prev) => ({
      ...prev,
      [sortField]: sortOrder === "asc" ? "desc" : "asc",
    }));

    dispatchAction(
      updateStateData({
        infra_sorting_measure: {
          sortField,
          sortOrder: sortOrder === "asc" ? "desc" : "asc",
        },
      })
    );

    const pageSize = pagination?.pageSize ?? 10;
    fetchStoreLocations(0, pageSize);
  };

  const initInfraLookups = React.useCallback(async () => {
    setBatchLoadingStatus(true)
    await Promise.all([
      fetchOpeningHours(),
      loadRegions(true),
      // fetchStoreNames(),
      // fetchOwnerNames("list-stores"),
      fetchCountriesList(),
      // fetchIpAddressForFilter(),
    ]).then(() => setBatchLoadingStatus(false));

    // scrollTableListings("stores_pagination");
  }, []);

  const { pagination, onPageChange, onPageSizeChange, refresh } = useListingTableController({
    paginationReduxKey: "stores_pagination",
    paginationCookieKey: "stores_pagination",
    managePaginationCookie: manageStoresPaginationCookie,
    loadPage: (pageNumber, pageSize) => fetchStoreLocations(pageNumber, pageSize).then(() => {
      setListLoaded(true);
    }),

    filtersReduxKey: "store_list_filters",
    filtersCookieKey: "store_list_filters",
    filterAppliedReduxKey: "store_list_filter_applied",

    onInit: initInfraLookups,
    onReset: reset_infrastructure_config_page_data,
    deletePaginationScope: "infrastructure",
    dataLoadedFlag: listLoaded
  });

  const columns = React.useMemo(
    () => [
      {
        id: "ip",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("ipAddress", sortingMeasure.ipAddress)} style={{ cursor: "pointer" }}>
            IP Address
            <SortIcon direction={sortingMeasure.ipAddress} />
          </ListingHeaderContent>
        ),
        render: (storeLoc) => (
          <ListingCellLeft>
            {storeLoc.store_ip ?? "-"}
          </ListingCellLeft>
        ),
      },
      {
        id: "name",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("storeName", sortingMeasure.storeName)} style={{ cursor: "pointer" }}>
            Name
            <SortIcon direction={sortingMeasure.storeName} />
          </ListingHeaderContent>
        ),
        cellClassName: "text-wrapper wrapLargeText",
        render: (storeLoc) => (
          <ListingCellLeft>
            {storeLoc.name}
          </ListingCellLeft>
        ),
      },
      {
        id: "owner",
        header: "Owner",
        cellClassName: "text-wrapper wrapLargeText",
        render: (storeLoc) => (
          <ListingCellLeft>
            {storeLoc?.company?.name}
          </ListingCellLeft>
        ),
      },
      {
        id: "devices",
        header: "Active Devices",
        headerAlign: "center",
        align: "center",
        render: (storeLoc) => (
          <ListingCellCenter>
            {/* ------- screens ---------- */}
            {storeLoc.active_screens_count === 0 ? (
              <HtmlTooltipDisabled title={"Screens"}>
                <ListingIconMetric>
                  <OndemandVideo sx={{ color: "#E6E6E6" }} />
                  <ListingMetricValue>
                    <IconTextDisabled variant="body2"> {storeLoc.active_screens_count} </IconTextDisabled>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltipDisabled>
            ) : (
              <HtmlTooltip title={"Screens"}>
                <ListingIconMetric>
                  <OndemandVideo sx={{ color: "#AF3241" }} />
                  <ListingMetricValue>
                    <IconText variant="body2"> {storeLoc.active_screens_count} </IconText>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltip>
            )}

            {/* ---------- area readers --------- */}
            {storeLoc.area_readers_count === 0 ? (
              <HtmlTooltipDisabled title={"Area readers"}>
                <ListingIconMetric>
                  <Router sx={{ color: "#E6E6E6" }} />
                  <ListingMetricValue>
                    <IconTextDisabled variant="body2"> {storeLoc.area_readers_count} </IconTextDisabled>
                  </ListingMetricValue>

                </ListingIconMetric>
              </HtmlTooltipDisabled>
            ) : (
              <HtmlTooltip title={"Area readers"}>
                <ListingIconMetric>
                  <Router sx={{ color: "#AF3241" }} />
                  <ListingMetricValue>
                    <IconText variant="body2"> {storeLoc.area_readers_count} </IconText>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltip>
            )}

            {/* -------- checkIn readers ----------- */}
            {storeLoc.checkin_readers_count === 0 ? (
              <HtmlTooltipDisabled title={"Checkin readers"}>
                <ListingIconMetric>
                  <Input sx={{ color: "#E6E6E6" }} />
                  <ListingMetricValue>
                    <IconTextDisabled variant="body2"> {storeLoc.checkin_readers_count} </IconTextDisabled>
                  </ListingMetricValue>

                </ListingIconMetric>
              </HtmlTooltipDisabled>
            ) : (
              <HtmlTooltip title={"Checkin readers"}>
                <ListingIconMetric>
                  <Input sx={{ color: "#AF3241" }} />
                  <ListingMetricValue>
                    <IconText variant="body2"> {storeLoc.checkin_readers_count} </IconText>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltip>
            )}
            {/* -------- checkout readers ----------- */}
            {storeLoc.checkout_readers_count === 0 ? (
              <HtmlTooltipDisabled title={"Checkout readers"}>
                <ListingIconMetric>
                  <Output sx={{ color: "#E6E6E6" }} />
                  <ListingMetricValue>
                    <IconTextDisabled variant="body2"> {storeLoc.checkout_readers_count} </IconTextDisabled>
                  </ListingMetricValue>

                </ListingIconMetric>
              </HtmlTooltipDisabled>
            ) : (
              <HtmlTooltip title={"Checkout readers"}>
                <ListingIconMetric>
                  <Output sx={{ color: "#AF3241" }} />
                  <ListingMetricValue>
                    <IconText variant="body2"> {storeLoc.checkout_readers_count} </IconText>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltip>
            )}
          </ListingCellCenter>
        ),
      },
      {
        id: "status",
        header: "Status",
        headerAlign: "center",
        align: "center",
        render: (storeLoc) => (
          <ListingCellCenter>
            {storeLoc.active === true ? (
              <HtmlTooltip title="Active">
                <CheckCircle sx={{ color: "#AF3241" }} />
              </HtmlTooltip>
            ) : (
              <HtmlTooltipDisabled title="Inactive">
                <Cancel sx={{ color: "#E6E6E6" }} />
              </HtmlTooltipDisabled>
            )}
          </ListingCellCenter>
        ),
      },
      {
        id: "details",
        header: "Details",
        headerAlign: "center",
        align: "center",
        render: (storeLoc) => (
          <ListingCellCenter>
            {permissionScope.infra_store_list_can_edit === true ||
              permissionScope.infra_store_detail_can_view === true ? (
              <>
                {storeLoc?.company?.level === 4 &&
                  ["branch"].includes(
                    loggedUserDetails.user_detail.user_type
                  ) ? (
                  <HtmlTooltipDisabled title="Not Available">
                    <Settings sx={{ color: "#E6E6E6"}} />
                  </HtmlTooltipDisabled>
                ) : (
                  <HtmlTooltip title="Edit">
                    <Settings
                      sx={{ color: "#AF3241", cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          "/infrastructure/configure-location/" + storeLoc.id
                        )
                      }
                    />
                  </HtmlTooltip>
                )}
              </>
            ) : (
              <HtmlTooltipDisabled title="No Access">
                <Settings sx={{ color: "#E6E6E6" }} />
              </HtmlTooltipDisabled>
            )}
          </ListingCellCenter>
        ),
      },
    ],
    [sortingMeasure, infraDetails?.infra_store_locs]);


  // download store list as excel file
  const exportToExcel = async () => {
    // Load store list
    const storeListResponse = await fetchStoreLocations(0, 5000, "download");

    const storesList = storeListResponse.store_location_list || [];

    const stores = storesList;// sortExcelStoreLocations(sortingParam, sortingMeasure, storesList);

    // Fetch current active schedule (multi-range capable) for all stores in one
    // call. The bulk /stores endpoint only returns the legacy single-range
    // opening_hours field, so we use the schedule-overviews bulk endpoint to get
    // accurate, multi-timeslot data for the export.
    const storeIds = stores.map((s) => s.id).filter((id) => id != null);
    const scheduleOverviews = await fetchScheduleOverviewsBulk(storeIds);

    const data = [];

    // Header row
    data.push([
      "IP Address",
      "Name",
      "Street Name & No.",
      "ZIP Code",
      "City",
      "Region",
      "Longitude",
      "Latitude",
      "Owner",
      "Screens",
      "Area Readers",
      "Checkin Readers",
      "Checkout Readers",
      "Active",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
      "Installed Trolleys",
      "Installed Baskets",
    ]);

    stores.forEach((store) => {
      const address = store.address || "";
      const zipCode = store.zipCode || store.zipcode || "";
      const city = store.city || "";
      const region = store.region?.name || "";
      const owner = store.company?.name || "";
      const storeIP = typeof store.store_ip !== "undefined" ? store.store_ip : "-"

      // Device counts
      const activeScreens = store.active_screens_count || 0;
      const checkoutReaders = store.checkout_readers_count || 0;
      const checkinReaders = store.checkin_readers_count || 0;
      const areaReaders = store.area_readers_count || 0;

      const isActive = store.active ? "Yes" : "No";
      const trolleys = store.installed_trolleys || 0;
      const baskets = store.installed_baskets || 0;

      // Prefer current_open from schedule/overview (new multi-range schedule).
      // Fall back to legacy store.opening_hours only if no schedule response.
      const overview = scheduleOverviews[store.id];
      const currentOpen = overview?.current_open || null;

      data.push([
        storeIP,
        store.name || "",
        address,
        zipCode,
        city,
        region,
        store.longitude || "",
        store.latitude || "",
        owner,
        activeScreens,
        areaReaders,
        checkinReaders,
        checkoutReaders,
        isActive,
        getHoursForExportDay(currentOpen, store.opening_hours, "monday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "tuesday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "wednesday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "thursday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "friday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "saturday"),
        getHoursForExportDay(currentOpen, store.opening_hours, "sunday"),
        trolleys,
        baskets,
      ]);
    });

    // Create a new workbook and add a worksheet
    const workbook = xlsx.utils.book_new();
    const worksheet = xlsx.utils.aoa_to_sheet(data);

    xlsx.utils.book_append_sheet(workbook, worksheet, "Store List");

    // Filename: [YYYYMMDD]_StoreList_[company name].xlsx
    const currentDate = checkTodayDateWithFormat("YYYYMMDD");
    const checkUserDetail = checkUserDetailsFromCookie();
    const companyName = checkUserDetail.user_detail.assigned_company.name.replaceAll(" ", "_") || "Company";
    const exportFileName = `${currentDate}_StoreList_${companyName}.xlsx`;

    xlsx.writeFile(workbook, exportFileName);
  };

  const formatHours = (hoursData) => {
    if (!hoursData) return "";
    // Old format: ["HH:mm", "HH:mm"]
    if (Array.isArray(hoursData) && hoursData.length === 2 && typeof hoursData[0] === "string") {
      if (hoursData[0] === "00:00" && hoursData[1] === "00:00") return "Closed";
      return `${hoursData[0]} - ${hoursData[1]}`;
    }
    // New format: [{open, close} | {startTime, endTime} | {start, end}]
    if (Array.isArray(hoursData) && hoursData.length > 0 && typeof hoursData[0] === "object") {
      const ranges = hoursData
        .map((r) => {
          const open = (r.open || r.startTime || r.start || "").slice(0, 5);
          const close = (r.close || r.endTime || r.end || "").slice(0, 5);
          if (!open && !close) return null;
          if (open === "00:00" && (close === "23:59" || close === "23:58")) return "Open 24 Hours";
          return `${open} - ${close}`;
        })
        .filter(Boolean);
      if (ranges.length === 0) return "Closed";
      return ranges.join(", ");
    }
    return "";
  };

  // Maps a weekday name to its hours from opening_hours regardless of whether
  // the API uses legacy string-keyed format {monday: [...]} or the new
  // numeric-keyed format {"1": [...]} (1=monday … 0=sunday per WEEKDAY_LOWER_TO_INT).
  const getHoursForDay = (openingHours, dayName) => {
    if (!openingHours) return "";
    // New format: numeric string key
    const numKey = String(WEEKDAY_LOWER_TO_INT[dayName]);
    if (openingHours[numKey] !== undefined) return formatHours(openingHours[numKey]);
    // Legacy format: weekday name key
    return formatHours(openingHours[dayName]);
  };

  // For the Excel export: prefers current_open from schedule/overview (UPPERCASE
  // day keys, multi-range [{start, end}]) and falls back to legacy opening_hours
  // when no schedule data is available for the store.
  const getHoursForExportDay = (currentOpen, legacyOpeningHours, dayName) => {
    if (currentOpen && typeof currentOpen === "object") {
      const upperKey = dayName.toUpperCase();
      if (Array.isArray(currentOpen[upperKey])) {
        return formatHours(currentOpen[upperKey]);
      }
    }
    return getHoursForDay(legacyOpeningHours, dayName);
  };

  return (
    <>
      <StyledMainBox component="main">
        <h1>Stores</h1>
        {canAddStore && (
          <Stack direction="row" spacing={2}>
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                openModal();
              }}
              label={"Add Store"}
              icon={<Add />}
            />
          </Stack>
        )}
        <Box height={30} />
        {/* ----------- search and add new store button -------------- */}
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          {
            typeof infraDetails.infra_store_locs !== "undefined" && (
              <Box width={1110}>
                <InfraFilters />
              </Box>
            )
          }
          {/* ------------ add new row and delete button ---------- */}
          {/* <Box display="flex" alignItems="center" ml={2}>
            
          </Box> */}
        </Box>
        {/* ============== store listing ============ */}
        <Box height={30} />
        <ListingDataTable
          columns={columns}
          rows={storeLocListitems}
          rowKey={(row) => `${row.id}_infra_store_row`}
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          footerRightActions={
            <TextButtonExtraLarge
              onClick={(e) => {
                e.preventDefault();
                exportToExcel();
              }}
              label={"Download Excel"}
              icon={<Download />}
            />
          }
          loading={typeof infraDetails.infra_store_locs === "undefined" ? true : infraDetails?.batchLoading}
        />

      </StyledMainBox>

      {/* ============ modal popup for different actions ========== */}
      {isModalOpen && (
        <AddStoreWizard open={isModalOpen} closeModal={closeModal} usedFor="add" />
      )}
    </>
  );
};

export default AccessControl(Locations, ["infra_store_list_can_view", "infra_store_list_can_edit"], [], ["infrastructure_section", "stores_infra"]);
